import {DestroyRef, Injectable, inject, signal} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {HttpErrorResponse} from "@angular/common/http";
import {
  EMPTY,
  Subject,
  catchError,
  concatMap,
  filter,
  from,
  fromEvent,
} from "rxjs";
import {
  DiscussionEmoji,
  DiscussionReactionLocal,
  DiscussionReactionState,
} from "../interfaces/discussion-reaction.interface";
import {DiscussionEntry} from "../interfaces/discussion.interface";
import {RecommendationThreadSummary} from "../interfaces/recommendation-thread.interface";
import {RecommendationDiscussionsService} from "./recommendation-discussions.service";

@Injectable()
export class DiscussionReactionsState {
  private readonly api = inject(RecommendationDiscussionsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly items = signal<Record<string, DiscussionReactionLocal>>({});
  readonly active = signal<string | null>(null);
  readonly changed$ = new Subject<DiscussionReactionState>();
  readonly rightsChanged$ = new Subject<void>();
  private threadId = "";
  private userId = "";
  private writable = false;
  private readonly invalidated = new Map<string, number>();
  private timer?: ReturnType<typeof setTimeout>;
  private reading = false;
  private refreshingRights = false;

  initialize(threadId: string, userId: string): void {
    this.threadId = threadId;
    this.userId = userId;
    this.api.reactions$
      .pipe(
        filter((event) => event.threadId === threadId),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        const key = event.target === "comment" ? "comment" : event.messageId;
        if (
          key &&
          this.items()[key] &&
          event.revision > this.items()[key].confirmed.revision
        )
          this.invalidate([key], event.revision);
      });
    fromEvent(document, "visibilitychange")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (document.visibilityState !== "visible") this.active.set(null);
      });
    this.destroyRef.onDestroy(() => {
      clearTimeout(this.timer);
      this.active.set(null);
      this.changed$.complete();
      this.rightsChanged$.complete();
    });
  }

  hydrate(
    thread: RecommendationThreadSummary,
    entries: DiscussionEntry[]
  ): void {
    this.refreshingRights = false;
    this.writable = thread.canReply;
    if (!this.writable) {
      this.active.set(null);
      this.items.update((items) =>
        Object.fromEntries(
          Object.entries(items).map(([key, item]) => [
            key,
            {...item, desired: undefined},
          ])
        )
      );
    }
    let status: DiscussionReactionState["status"] = "available";
    if (!thread.comment?.trim()) status = "unavailable";
    if (thread.commentModeratedAt) status = "moderated";
    this.reconcileState({
      target: "comment",
      status,
      reactions: thread.commentReactions ?? [],
      revision: thread.commentReactionsRevision ?? 0,
      moderatedAt: thread.commentModeratedAt,
    });
    for (const entry of entries) {
      if (entry.kind !== "message") continue;
      status = "available";
      if (entry.deletedAt) status = "deleted";
      if (entry.moderatedAt) status = "moderated";
      this.reconcileState({
        target: "message",
        messageId: entry._id,
        status,
        reactions: entry.reactions ?? [],
        revision: entry.reactionsRevision ?? 0,
        deletedAt: entry.deletedAt,
        moderatedAt: entry.moderatedAt,
      });
    }
  }

  view(key: string): DiscussionReactionLocal | undefined {
    const item = this.items()[key];
    if (
      !item ||
      item.desired === undefined ||
      item.confirmed.status !== "available"
    )
      return item;
    const reactions = item.confirmed.reactions.filter(
      (reaction) => reaction.userId !== this.userId
    );
    if (item.desired)
      reactions.push({userId: this.userId, emoji: item.desired});
    return {...item, confirmed: {...item.confirmed, reactions}};
  }

  choose(key: string, emoji: DiscussionEmoji, gem = false): void {
    const current = this.view(key)?.confirmed.reactions.find(
      (reaction) => reaction.userId === this.userId
    )?.emoji;
    this.desire(key, !gem && current === emoji ? null : emoji);
    this.active.set(null);
  }

  retry(key: string): void {
    const item = this.items()[key];
    if (item?.reconciling) this.invalidate([key]);
    else if (item?.retry !== undefined) this.desire(key, item.retry);
  }

  cancel(key: string): void {
    this.update(key, {desired: undefined});
    if (this.active() === key) this.active.set(null);
  }

  invalidate(keys = Object.keys(this.items()), revision = Infinity): void {
    if (this.destroyRef.destroyed) return;
    for (const key of keys)
      if (this.items()[key])
        this.invalidated.set(
          key,
          Math.max(this.invalidated.get(key) ?? 0, revision)
        );
    if (this.timer || this.reading || !this.invalidated.size) return;
    this.timer = setTimeout(() => {
      this.timer = undefined;
      this.reconcile();
    }, 120);
  }

  private desire(key: string, desired: DiscussionEmoji | null): void {
    const item = this.items()[key];
    if (
      !this.writable ||
      !item ||
      item.confirmed.status !== "available" ||
      item.reconciling
    )
      return;
    this.update(key, {desired, error: undefined, retry: undefined});
    this.send(key);
  }

  private send(key: string): void {
    const item = this.items()[key];
    if (
      !this.writable ||
      !item ||
      item.saving ||
      item.reconciling ||
      item.confirmed.status !== "available" ||
      item.desired === undefined
    )
      return;
    const desired = item.desired;
    const current =
      item.confirmed.reactions.find(
        (reaction) => reaction.userId === this.userId
      )?.emoji ?? null;
    if (desired === current) {
      this.update(key, {desired: undefined});
      return;
    }
    this.update(key, {saving: true});
    this.api
      .react(this.threadId, key, desired)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (state) => {
          this.merge(state);
          const latest = this.items()[key];
          this.update(key, {
            saving: false,
            desired: latest.desired === desired ? undefined : latest.desired,
          });
          this.send(key);
        },
        error: (error: unknown) => {
          if (this.items()[key]?.confirmed.status !== "available") {
            this.update(key, {saving: false});
            return;
          }
          const latest = this.items()[key]?.desired;
          const status = error instanceof HttpErrorResponse ? error.status : 0;
          const unavailable = status === 404 || status === 409;
          this.update(key, {
            saving: false,
            desired: undefined,
            retry: latest === undefined ? desired : latest,
            reconciling: true,
            error: unavailable
              ? "DISCUSSION.REACTION_UNAVAILABLE"
              : "DISCUSSION.REACTION_ERROR",
          });
          this.active.set(null);
          if (status === 403 || unavailable) this.refreshRights();
          this.invalidate([key]);
        },
      });
  }

  private reconcile(): void {
    const keys = [...this.invalidated.keys()];
    this.invalidated.clear();
    if (!keys.length) return;
    this.reading = true;
    const chunks: string[][] = [];
    for (let i = 0; i < keys.length; i += 50)
      chunks.push(keys.slice(i, i + 50));
    from(chunks)
      .pipe(
        concatMap((chunk) =>
          this.api.readReactions(this.threadId, chunk).pipe(
            catchError((error: unknown) => {
              for (const key of chunk)
                this.update(key, {
                  reconciling: true,
                  error: "DISCUSSION.REACTION_SYNC_ERROR",
                });
              if (
                error instanceof HttpErrorResponse &&
                (error.status === 403 || error.status === 404)
              )
                this.refreshRights();
              return EMPTY;
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (states) => {
          for (const state of states) this.reconcileState(state);
        },
        complete: () => {
          this.reading = false;
          this.invalidate([]);
        },
      });
  }

  private reconcileState(state: DiscussionReactionState): void {
    this.merge(state);
    const key = this.key(state);
    const item = this.items()[key];
    const current =
      item.confirmed.reactions.find(
        (reaction) => reaction.userId === this.userId
      )?.emoji ?? null;
    const settled =
      item.retry === undefined ||
      item.retry === current ||
      item.confirmed.status !== "available";
    this.update(key, {
      reconciling: false,
      retry: settled ? undefined : item.retry,
      error: settled ? undefined : "DISCUSSION.REACTION_ERROR",
    });
  }

  private merge(state: DiscussionReactionState): void {
    const key = this.key(state);
    const old = this.items()[key];
    if (
      old?.confirmed.status !== undefined &&
      old.confirmed.status !== "available" &&
      state.status === "available"
    )
      return;
    if (
      old &&
      state.revision < old.confirmed.revision &&
      state.status === "available"
    )
      return;
    const terminal = state.status !== "available";
    if (terminal || state.revision >= (this.invalidated.get(key) ?? Infinity))
      this.invalidated.delete(key);
    const confirmed = {
      ...state,
      revision: Math.max(state.revision, old?.confirmed.revision ?? 0),
      reactions: terminal ? [] : state.reactions,
    };
    this.items.update((items) => ({
      ...items,
      [key]: {
        ...old,
        saving: old?.saving ?? false,
        confirmed,
        desired: terminal ? undefined : old?.desired,
        retry: terminal ? undefined : old?.retry,
        error: terminal ? undefined : old?.error,
      },
    }));
    if (terminal && this.active() === key) this.active.set(null);
    this.changed$.next(confirmed);
  }

  private update(key: string, patch: Partial<DiscussionReactionLocal>): void {
    this.items.update((items) =>
      items[key] ? {...items, [key]: {...items[key], ...patch}} : items
    );
  }

  private refreshRights(): void {
    if (this.refreshingRights) return;
    this.refreshingRights = true;
    this.rightsChanged$.next();
  }

  private key(state: DiscussionReactionState): string {
    return state.target === "comment" ? "comment" : state.messageId!;
  }
}
